/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

/**
 * useStepEnricher - Block Step Enhancement Hook
 *
 * Enriches block steps with metadata and code information from live editor,
 * cached content, or block definitions. Handles nested block step updates.
 */

import { useTabManagerContext } from '../context/TabManagerContext';
import { StepConverter } from '../managers/StepConverter';

import { toast } from '@/components/ui/use-toast';
import { Step as UIStep } from '@/pages/projects/LiveAgentTypes';

export const useStepEnricher = () => {
    const { blocksData, astParser, tabContentRef, activeTabIdRef, editorRef } = useTabManagerContext();

    /**
     * Helper function to get block code from live editor, cached content, or blocksData
     * @param blockName - Name of the block to get code for
     * @returns Block code or null if not found
     */
    const getBlockCode = (blockName: string): string | null => {
        const blockTabId = `block-${blockName}`;
        const isCurrentlyActiveBlockTab = activeTabIdRef.current === blockTabId;

        // 1. Try live editor first
        if (isCurrentlyActiveBlockTab && editorRef.current) {
            return editorRef.current.getValue();
        }

        // 2. Try cached content
        const cachedContent = tabContentRef.current.get(blockTabId);
        if (cachedContent) {
            return cachedContent;
        }

        // 3. Fall back to blocksData
        if (blocksData) {
            const matchingBlock = blocksData.find((block) => !block.archived && block.name === blockName);
            if (matchingBlock) {
                const blockCode = matchingBlock.steps
                    ? StepConverter.stepsToCode(matchingBlock.steps)
                    : '// Block implementation goes here';

                const paramNames = (matchingBlock.blockDefArgs || []).join(', ');
                return `Heal.blocks.${blockName} = async (${paramNames}) => {\n${blockCode}\n};`;
            }
        }

        return null;
    };

    /**
     * Enrich block steps with metadata and code information
     * Priority: live editor → cached content → block definitions
     * @param inputSteps - Steps to enrich
     * @param visitedBlocks - Set of visited blocks (for circular dependency detection)
     * @returns Object with enriched steps and flattened innerBlocksCode from all blocks
     */
    const enrichBlockSteps = (
        inputSteps: UIStep[],
        visitedBlocks: Set<string> = new Set(),
    ): { steps: UIStep[]; innerBlocksCode: Record<string, string> } => {
        const result = [];
        const allInnerBlocksCode: Record<string, string> = {};

        for (const step of inputSteps) {
            if (step.kind === 'block' && step.name) {
                // Check for circular dependency
                if (visitedBlocks.has(step.name)) {
                    toast({
                        title: 'Error: Circular dependency detected',
                        description: `Block "${step.name}" depends on itself`,
                        variant: 'destructive',
                    });
                    result.push(step); // Return original step without enrichment
                    continue;
                }

                // Add current block to visited set
                const newVisitedBlocks = new Set(visitedBlocks);
                newVisitedBlocks.add(step.name);

                // Get block code using helper function
                const blockCode = getBlockCode(step.name);
                let enrichedStep = step;

                if (blockCode) {
                    const blockFunctionInfo = astParser.extractBlockFunctionInfo(blockCode);
                    if (blockFunctionInfo?.args) {
                        // Collect inner blocks code for this block
                        const innerBlocksCode: Record<string, string> = {};
                        for (const innerBlockName of blockFunctionInfo.innerBlocks) {
                            const innerBlockCode = getBlockCode(innerBlockName);
                            if (innerBlockCode) {
                                innerBlocksCode[innerBlockName] = innerBlockCode;
                                // Add to global collection
                                allInnerBlocksCode[innerBlockName] = innerBlockCode;
                            }
                        }

                        enrichedStep = {
                            ...step,
                            blockDefArgs: blockFunctionInfo.args,
                            blockHasReturnValue: blockFunctionInfo.hasReturnValue || false,
                            blockCode,
                            innerBlocksCode,
                        };
                    }
                }

                // Recursively enrich nested block steps if they exist
                if (enrichedStep.steps) {
                    const nestedResult = enrichBlockSteps(enrichedStep.steps, newVisitedBlocks);
                    enrichedStep = {
                        ...enrichedStep,
                        steps: nestedResult.steps,
                        innerBlocksCode: { ...nestedResult.innerBlocksCode, ...enrichedStep.innerBlocksCode },
                    };
                }

                result.push(enrichedStep);
            } else {
                result.push(step);
            }
        }

        return { steps: result, innerBlocksCode: allInnerBlocksCode };
    };

    /**
     * Recursively update nested block steps
     * @param steps - Steps to update
     * @param blockName - Name of block to update
     * @param blockSteps - New steps for the block
     * @param visitedBlocks - Set of visited blocks (for circular dependency detection)
     * @returns Updated steps, or original steps if circular dependency detected
     */
    const updateNestedBlockSteps = (steps: UIStep[], blockName: string, blockSteps: UIStep[]): UIStep[] => {
        return steps.map((step) => {
            if (step.kind === 'block' && step.name) {
                if (step.name === blockName) {
                    return { ...step, steps: blockSteps };
                } else if (step.steps) {
                    return {
                        ...step,
                        steps: updateNestedBlockSteps(step.steps, blockName, blockSteps),
                    };
                }
            }
            return step;
        });
    };

    return {
        enrichBlockSteps: (inputSteps: UIStep[], visitedBlocks: Set<string> = new Set()): UIStep[] => {
            // Wrapper to maintain backward compatibility - returns only steps
            return enrichBlockSteps(inputSteps, visitedBlocks).steps;
        },
        updateNestedBlockSteps,
    };
};
